import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Bed, Maximize2, Layers } from "lucide-react";
import { families, getProductsByFamily } from "../data/products";
import useSEO from "../hooks/useSEO";

const VIDEO_URL = "/videos/home-hero.mp4";

const POSTER =
  "/images/9y2itsq4-image-1.webp";

function CardStat({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-[11px] tracking-[0.12em] uppercase text-[var(--yono-warm-grey)]">
      <Icon size={14} strokeWidth={1.4} className="text-[var(--yono-wood)]" />
      <span>{label}</span>
    </div>
  );
}

function ProductCard({ p }) {
  return (
    <div className="flex flex-col" data-testid={`product-card-${p.slug}`}>
      <Link to={`/produits/${p.slug}`} className="block hover-lift">
        <div className="relative aspect-[4/3] product-card-img overflow-hidden">
          {(p.cardImage || p.hero) ? (
            <img
              src={p.cardImage || p.hero}
              alt={`${p.name} — habitat bois modulaire YONO`}
              className={`w-full h-full ${p.cardFit === "contain" ? "object-contain" : "object-cover"}`}
            />
          ) : (
            <div className="absolute inset-0 placeholder-bois flex flex-col items-center justify-center">
              <span className="font-mono text-[11px] uppercase tracking-widest text-[var(--yono-warm-grey)] mb-2">
                Visuel à venir
              </span>
              <span className="font-heading text-2xl">{p.name}</span>
            </div>
          )}
        </div>
      </Link>
      <div className="mt-6">
        <div className="eyebrow text-[var(--yono-wood)]">YONO</div>
        <h3 className="font-heading text-[24px] leading-none mt-2">{p.name}</h3>
        <p className="text-[14px] text-[var(--yono-warm-grey)] mt-2">
          {p.tagline}
        </p>
        <div className="flex flex-wrap gap-x-5 gap-y-2 mt-5">
          {p.stats?.map((s, i) => (
            <CardStat key={i} icon={s.icon} label={s.label} />
          ))}
        </div>
        <div className="flex items-center justify-between mt-7">
          <span className="text-[15px] font-medium leading-tight">
            Prix sur devis
            <span className="block text-[11px] tracking-[0.05em] normal-case text-[var(--yono-warm-grey)] mt-1 font-normal">
              Selon configuration, équipements et livraison.
            </span>
          </span>
          <Link
            to={`/produits/${p.slug}`}
            className="btn-primary !py-[10px] !px-[20px] !text-[11px]"
            data-testid={`card-cta-${p.slug}`}
          >
            En savoir plus <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    </div>
  );
}

// Static stats config matching yono-studio.com format
const PRODUCT_STATS = {
  "yono-micro": [
    { icon: Bed, label: "1 à 2 pers." },
    { icon: Maximize2, label: "6 m²" },
    { icon: Layers, label: "CLT bois massif" },
  ],
  "yono-studio": [
    { icon: Bed, label: "2 pers." },
    { icon: Maximize2, label: "13 m²" },
    { icon: Layers, label: "CLT 60 mm" },
  ],
  "yono-nido": [
    { icon: Bed, label: "2 à 4 pers." },
    { icon: Maximize2, label: "13 m²" },
    { icon: Layers, label: "CLT 60 mm" },
  ],
  "yono-lodge": [
    { icon: Bed, label: "4 pers." },
    { icon: Maximize2, label: "22 m²" },
    { icon: Layers, label: "CLT bois massif" },
  ],
  "yono-family-lodge": [
    { icon: Bed, label: "6 pers." },
    { icon: Maximize2, label: "32 m² et +" },
    { icon: Layers, label: "2 volumes + véranda" },
  ],
  "yono-twin": [
    { icon: Bed, label: "2 chambres" },
    { icon: Maximize2, label: "13 m²" },
    { icon: Layers, label: "CLT 60 mm" },
  ],
};

export default function Home() {
  useSEO({
    title: "Habitat bois modulaire pour campings & PRL",
    description:
      "YONO est une gamme française d'habitats bois modulaires conçue et fabriquée en France pour les campings, PRL, domaines nature et projets d'hébergement professionnel. Découvrez YONO Micro, Studio, Nido, Lodge T3, Lodge T4 et Twin.",
    path: "/",
    keywords:
      "YONO, YONO habitat, YONO habitat bois, YONO camping, YONO lodge, YONO studio, YONO HPA, YONO Ideal Tiny, module YONO, marque YONO, habitat bois professionnel, hébergement bois camping, CLT, hôtellerie de plein air, PRL, domaine nature, mobile home bois, hébergement saisonnier",
  });

  return (
    <div data-testid="home-page">
      {/* HERO — overlay on desktop, stacked on mobile */}
      <section className="relative w-full overflow-hidden hidden lg:block bg-[var(--yono-soft-black)]" style={{ height: "calc(100vh - 112px)", minHeight: 540, maxHeight: 820 }}>
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="home-hero-video"
        >
          <source src={VIDEO_URL} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
        <div className="relative h-full container-yono flex flex-col justify-end pb-10 lg:pb-12">
          <div className="max-w-xl text-white fade-up">
            <h1 className="font-heading text-[22px] sm:text-[26px] lg:text-[28px] xl:text-[30px] leading-[1.15] tracking-tight">
              Créer un nouvel espace d'accueil, en bois
            </h1>
            <p className="mt-5 text-[15px] leading-[1.7] text-white/90 max-w-xl">
              YONO conçoit des habitats bois compacts et haut de gamme, prêts à
              raccorder, pour les professionnels de l'hôtellerie de plein air,
              les campings, les PRL et les domaines nature. Architecture sobre,
              matériaux biosourcés, fabrication française.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/gamme" className="btn-primary !bg-white !text-[var(--yono-soft-black)] !border-white" data-testid="hero-decouvrir-btn">
                Découvrir la gamme <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/contact" className="btn-outline-light" data-testid="hero-contact-btn">
                Étudier mon projet
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile hero: video on top, text below */}
      <section className="lg:hidden">
        <div className="relative w-full overflow-hidden bg-[var(--yono-soft-black)]" style={{ height: "62vh", minHeight: 360 }}>
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="home-hero-video-mobile"
          >
            <source src={VIDEO_URL} type="video/mp4" />
          </video>
        </div>
        <div className="container-yono pt-8 pb-10">
          <h2 className="font-heading text-[26px] sm:text-[32px] leading-[1.12] tracking-tight text-[var(--yono-soft-black)]">
            Créer un nouvel espace d'accueil, en bois
          </h2>
          <p className="mt-5 text-[15px] leading-[1.7] text-[var(--yono-soft-black)]/85">
            YONO conçoit des habitats bois compacts et haut de gamme, prêts à
            raccorder, pour les professionnels de l'hôtellerie de plein air,
            les campings, les PRL et les domaines nature. Architecture sobre,
            matériaux biosourcés, fabrication française.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/gamme" className="btn-primary" data-testid="hero-decouvrir-btn-mobile">
              Découvrir la gamme <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
            <Link to="/contact" className="btn-outline" data-testid="hero-contact-btn-mobile">
              Étudier mon projet
            </Link>
          </div>
        </div>
      </section>

      {/* LA MARQUE */}
      <section className="section" data-testid="brand-section">
        <div className="container-yono">
          <div className="eyebrow mb-8">La marque</div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <h2 className="lg:col-span-7 font-heading text-[26px] sm:text-[30px] lg:text-[36px] leading-[1.18] tracking-tight">
              Des habitats compacts, chaleureux et durables
            </h2>
            <div className="lg:col-span-5 text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/85 space-y-4">
              <p>
                YONO conçoit des habitats bois compacts, déplaçables et prêts à
                raccorder, pensés pour les professionnels de l'hébergement.
              </p>
              <p>
                Installables rapidement, ils permettent d'équiper des
                emplacements nus, de créer de nouvelles unités locatives,
                d'accueillir des saisonniers ou de développer une offre
                d'hébergement durable, confortable et différenciante.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* LA GAMME - beige background, grouped by family */}
      <section className="section bg-[var(--yono-beige)]" data-testid="gamme-section">
        <div className="container-yono">
          <div className="eyebrow mb-6">La gamme</div>
          <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] leading-[1.18] tracking-tight max-w-3xl mb-4">
            Trois familles, six solutions
          </h2>
          <p className="text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/80 max-w-2xl mb-14">
            Micro-habitats à forte rentabilité, lodges compacts en alternative
            au mobil-home, et hébergements dédiés au logement des saisonniers.
          </p>

          <div className="space-y-14 lg:space-y-20">
            {families.map((f) => {
              const items = getProductsByFamily(f.slug).map((p) => ({
                ...p,
                stats: PRODUCT_STATS[p.slug],
              }));
              const gridCols =
                items.length >= 3
                  ? "md:grid-cols-2 lg:grid-cols-3"
                  : items.length === 2
                  ? "md:grid-cols-2"
                  : "md:grid-cols-2 lg:grid-cols-3";
              return (
                <div key={f.slug} data-testid={`home-family-${f.slug}`}>
                  <div className="border-t border-[var(--yono-soft-black)]/15 pt-5 mb-8 lg:mb-10 flex items-baseline justify-between gap-6">
                    <div>
                      <p className="eyebrow text-[var(--yono-wood)] mb-1">
                        Famille {f.index}
                      </p>
                      <h3 className="font-heading text-[22px] sm:text-[26px] tracking-tight">
                        {f.name}
                      </h3>
                    </div>
                    <p className="hidden md:block text-[13px] leading-[1.6] text-[var(--yono-warm-grey)] max-w-sm text-right">
                      {f.short}
                    </p>
                  </div>
                  <div className={`grid grid-cols-1 ${gridCols} gap-x-7 gap-y-14`}>
                    {items.map((p) => (
                      <ProductCard key={p.slug} p={p} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-14 lg:mt-16 flex flex-wrap gap-3">
            <Link to="/gamme" className="btn-outline" data-testid="home-voir-gamme-btn">
              Découvrir la gamme <ArrowRight size={14} strokeWidth={1.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* LES USAGES */}
      <section className="section" data-testid="usages-section">
        <div className="container-yono">
          <div className="eyebrow mb-6">Les marchés</div>
          <h2 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] leading-[1.15] tracking-tight max-w-3xl mb-4">
            Une gamme pensée pour les exploitants
          </h2>
          <p className="text-[15px] leading-[1.85] text-[var(--yono-soft-black)]/80 max-w-2xl mb-14">
            Pensés comme des habitats compacts et prêts à raccorder, les
            solutions YONO répondent aux besoins concrets des professionnels de
            l'hébergement : équiper un emplacement nu, renouveler une offre
            locative, loger des saisonniers ou créer un véritable logement bois.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--yono-border)] border border-[var(--yono-border)]">
            {[
              {
                t: "Campings & hôtellerie de plein air",
                d: "Créer de nouvelles capacités d'accueil, renouveler une offre locative ou proposer un hébergement compact et premium.",
              },
              {
                t: "PRL & domaines nature",
                d: "Solutions adaptées aux projets touristiques sobres, intégrés au paysage et orientés nature, en plain-pied ou en deux volumes.",
              },
              {
                t: "Logement saisonnier",
                d: "Loger des saisonniers, équipes en déplacement ou publics en transition avec des habitats confortables et durables.",
              },
            ].map((u) => (
              <div key={u.t} className="bg-[var(--yono-off-white)] p-10">
                <h3 className="font-heading text-xl mb-3">{u.t}</h3>
                <p className="text-[14px] leading-relaxed text-[var(--yono-warm-grey)]">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Piliers */}
      <section className="section bg-[var(--yono-beige)]">
        <div className="container-yono">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {[
              { t: "Bois massif CLT", d: "Matériaux biosourcés pour des habitats durables et sains." },
              { t: "Design architectural", d: "Une esthétique sobre et intemporelle, signée YONO." },
              { t: "Qualité de fabrication", d: "Préfabrication en atelier, contrôle qualité rigoureux." },
              { t: "Prêt à raccorder", d: "Livraison clé en main, raccordement simplifié." },
            ].map((b, i) => (
              <div key={b.t}>
                <div className="font-mono text-[11px] tracking-[0.12em] text-[var(--yono-wood)] mb-3">
                  0{i + 1}
                </div>
                <h3 className="font-heading text-xl mb-3">{b.t}</h3>
                <p className="text-[14px] leading-relaxed text-[var(--yono-warm-grey)]">{b.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-yono">
          <div className="border border-[var(--yono-border)] p-12 lg:p-20 text-center">
            <h3 className="font-heading text-[26px] sm:text-[30px] lg:text-[36px] leading-tight tracking-tight max-w-3xl mx-auto">
              Un projet d'hébergement ou un parc à déployer ?
            </h3>
            <p className="mt-5 text-[var(--yono-warm-grey)] max-w-2xl mx-auto leading-relaxed text-[15px]">
              Échangeons sur votre besoin, le nombre de logements envisagé, les
              contraintes de votre site et le calendrier souhaité.
            </p>
            <div className="mt-9 flex flex-wrap justify-center gap-3">
              <Link to="/contact" className="btn-primary" data-testid="cta-contact-btn">
                Étudier mon projet
              </Link>
              <Link to="/gamme" className="btn-outline" data-testid="cta-produits-btn">
                Voir la gamme
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
